/**
 * Offer-related API operations
 */
import { del, get, post, put, request, unwrapData } from './base';
import type {
  Benefit,
  Bonus,
  Candidate,
  Commission,
  CompanyInfo,
  CompensationType,
  EmploymentType,
  EquityDetails,
} from './types';

// Offer submission interface
export interface OfferSubmitData {
  candidate: Candidate;
  role: string;
  department: string;
  team?: string;
  location: string;
  start_date: string;
  reporting_to?: string;
  status?: 'published';
  expires_at: string;
  base_salary: number;
  payment_frequency?: 'weekly' | 'biweekly' | 'semi_monthly' | 'monthly';
  bonuses?: Bonus[];
  // null is meaningful: it removes an existing commission structure.
  commission?: Commission | null;
  currency: string;
  welcome_message?: string;
  // null is meaningful: it removes an existing equity grant from the offer.
  equity_details?: EquityDetails | null;
  benefits?: Benefit[];
  company_info?: CompanyInfo;
  company_branding_id?: string;
  employment_type?: EmploymentType;
  compensation_type?: CompensationType;
  // null is meaningful and is how these two CLEAR: both are nullable columns,
  // and DRF's DecimalField/DateField reject '' outright. Omitting the key
  // means "leave it alone", which is why a blanked field used to survive the
  // save (see `getOfferData`'s clearable-scalar block).
  hourly_rate?: number | null;
  end_date?: string | null;
}

// Offer response interface
export interface OfferResponse {
  id: string;
  status: string;
  created_at: string;
  made_at: string;
  expires_at: string;
  start_date: string | null;
  published_at?: string | null;
  accepted_at?: string | null;
  declined_at?: string | null;
  opened_first_time_at: string | null;
  last_viewed_at: string | null;
  base_salary: number;
  payment_frequency: 'weekly' | 'biweekly' | 'semi_monthly' | 'monthly';
  bonuses?: Bonus[];
  commission?: Commission;
  ote?: number;
  currency: string;
  role: string;
  department: string;
  team?: string | null;
  location: string;
  reporting_to: string | null;
  welcome_message: string | null;
  candidate: Candidate;
  candidate_name?: string;
  candidate_email?: string;
  created_by: {
    first_name: string;
    last_name: string;
    email: string;
    phone_number?: string;
    avatar?: string | null;
    job_title?: string | null;
  } | null;
  company: CompanyInfo;
  equity_details: EquityDetails | null;
  benefits?: Benefit[];
  nda_required?: boolean;
  nda_signed?: boolean;
  nda_id?: string;
  nda_content?: string;
  nda_title?: string;
  /** True when a legal document is attached (drives the "Review Offer" CTA). */
  has_document?: boolean;
  /** True when an attached document has been fully signed and sealed. */
  document_completed?: boolean;
  employment_type?: EmploymentType;
  compensation_type?: CompensationType;
  hourly_rate?: number;
  end_date?: string;
}

export type Offer = OfferResponse;

// Offer list item interface
export interface OfferListItem {
  id: string;
  candidate: {
    name: string;
    email: string;
  };
  role: string;
  department: string;
  team?: string | null;
  salary: number;
  created_at: string;
  published_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  opened_first_time_at: string | null;
  last_viewed_at: string | null;
  expires_at?: string | null;
  status: string;
  employment_type?: EmploymentType;
  compensation_type?: CompensationType;
  hourly_rate?: number;
  end_date?: string;
  /**
   * Badge-grade document status from the list query's scalar annotations.
   * `null` = no document (render a muted em-dash, never a chip). The drawer's
   * status endpoint is the authoritative view.
   */
  document_status?: DocumentBadgeStatus | null;
}

// Extended offer list item interface with company information
export interface OfferListItemWithCompany extends OfferListItem {
  company: {
    id: string;
    name: string;
  };
}

export interface OfferDeleteResult {
  id: string;
}

const toNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  return typeof value === 'string' ? Number(value) : value;
};

const deriveCandidateName = (offer: Offer): string => {
  if (offer.candidate_name) {
    return offer.candidate_name;
  }

  const candidate = offer.candidate as unknown as Record<string, unknown> | undefined;

  if (!candidate) {
    return '';
  }

  const first = (candidate.first_name as string | undefined) ?? '';
  const last = (candidate.last_name as string | undefined) ?? '';
  const combined = `${first} ${last}`.trim();

  if (combined) {
    return combined;
  }

  if (typeof candidate.name === 'string') {
    return candidate.name;
  }

  if (typeof candidate.email === 'string') {
    return candidate.email;
  }

  return '';
};

const deriveCandidateEmail = (offer: Offer): string => {
  if (offer.candidate_email) {
    return offer.candidate_email;
  }

  const candidate = offer.candidate as unknown as Record<string, unknown> | undefined;

  if (candidate && typeof candidate.email === 'string') {
    return candidate.email;
  }

  return '';
};

const mapOfferToListItem = (offer: Offer): OfferListItem => {
  const candidateName = deriveCandidateName(offer);

  return {
    id: offer.id,
    candidate: {
      name: candidateName,
      email: deriveCandidateEmail(offer),
    },
    role: offer.role,
    department: offer.department ?? '',
    team: offer.team ?? null,
    salary: toNumber(offer.base_salary),
    created_at: offer.created_at,
    published_at: offer.published_at ?? null,
    accepted_at: offer.accepted_at ?? null,
    declined_at: offer.declined_at ?? null,
    opened_first_time_at: offer.opened_first_time_at ?? null,
    last_viewed_at: offer.last_viewed_at ?? null,
    expires_at: offer.expires_at ?? null,
    status: offer.status,
    document_status:
      (offer as unknown as { document_status?: DocumentBadgeStatus | null }).document_status ??
      null,
  };
};

const mapOfferToListItemWithCompany = (offer: Offer): OfferListItemWithCompany => ({
  ...mapOfferToListItem(offer),
  company: {
    id: offer.company?.id ?? '',
    name: offer.company?.name ?? '',
  },
});

export interface OfferLinkContext {
  candidate_name: string;
  candidate_email: string;
  role: string;
  company_name: string;
  expiry_date: string;
}

export interface OfferLinkResult {
  message?: string;
  offer_url: string;
  context: OfferLinkContext;
}

// Validation function (currently unused but kept for future use)
const _validateOfferData = (data: OfferSubmitData): string | null => {
  if (!data.candidate.first_name?.trim()) {
    return 'Candidate first name is required';
  }
  if (!data.candidate.last_name?.trim()) {
    return 'Candidate last name is required';
  }
  if (!data.candidate.email?.trim()) {
    return 'Candidate email is required';
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(data.candidate.email)) {
    return 'Please enter a valid email address';
  }
  if (!data.role?.trim()) {
    return 'Role is required';
  }
  if (!data.department?.trim()) {
    return 'Department is required';
  }
  if (!data.location?.trim()) {
    return 'Location is required';
  }
  if (!data.start_date?.trim()) {
    return 'Start date is required';
  }
  if (!data.expires_at?.trim()) {
    return 'Expiration date is required';
  }

  // Validate compensation based on type
  const compensationType = data.compensation_type || 'salary';
  if (compensationType === 'salary') {
    if (!data.base_salary || data.base_salary <= 0) {
      return 'Base salary must be greater than 0';
    }
  } else if (compensationType === 'hourly') {
    if (!data.hourly_rate || data.hourly_rate <= 0) {
      return 'Hourly rate must be greater than 0';
    }
  }

  // Validate bonuses if they exist
  if (data.bonuses && data.bonuses.length > 0) {
    for (const bonus of data.bonuses) {
      if (!bonus.type) {
        return 'Bonus type is required';
      }
      if (!bonus.amount || bonus.amount <= 0) {
        return `Amount for ${bonus.type} bonus must be greater than 0`;
      }
    }
  }

  if (data.equity_details) {
    if (!data.equity_details.number_of_shares || data.equity_details.number_of_shares <= 0) {
      return 'Number of shares must be greater than 0';
    }
    if (
      data.equity_details.equity_type === 'stock_options' &&
      (!data.equity_details.strike_price || data.equity_details.strike_price <= 0)
    ) {
      return 'Strike price must be greater than 0 for stock options';
    }
    if (!data.equity_details.preferred_price || data.equity_details.preferred_price <= 0) {
      return 'Preferred price must be greater than 0';
    }
  }
  return null;
};

/**
 * Submit an offer to the API for publishing
 */
export const submitOffer = async (offerData: OfferSubmitData): Promise<Offer> => {
  return post<Offer>('/offers/create/', offerData);
};

/**
 * Get a specific offer by ID
 */
export const getOffer = async (offerId: string): Promise<OfferResponse> => {
  const result = await get<OfferResponse>(`/offers/${offerId}/`);

  result.candidate_name = deriveCandidateName(result);
  result.candidate_email = deriveCandidateEmail(result);

  return result;
};

/**
 * Get offers for the current user
 */
export const getUserOffers = async (): Promise<OfferListItem[]> => {
  const { items } = await get<{ items: Offer[] }>('/offers/');
  return (items ?? []).map(mapOfferToListItem);
};

/**
 * Get all offers from all companies for agency recruiters
 * Only available to users with AgencyRecruiter role
 */
export const getAllOffers = async (): Promise<OfferListItemWithCompany[]> => {
  const { items } = await get<{ items: Offer[] }>('/offers/all/');
  return (items ?? []).map(mapOfferToListItemWithCompany);
};

/**
 * Get offer with signature verification (public view)
 */
export const getOfferWithSignature = async (signedData: string): Promise<OfferResponse> => {
  const result = await get<OfferResponse>(`/offers/view/${encodeURIComponent(signedData)}/`);

  result.candidate_name = deriveCandidateName(result);
  result.candidate_email = deriveCandidateEmail(result);

  return result;
};

/**
 * Decline an offer (authenticated)
 */
export const declineOffer = async (
  offerId: string,
  reason?: string
): Promise<{ status: string }> => {
  const payload = reason ? { reason } : undefined;
  return post<{ status: string }>(`/offers/${offerId}/decline/`, payload);
};

/**
 * Decline an offer with signature (public)
 */
export const declineOfferWithSignature = async (
  signedData: string,
  reason?: string
): Promise<{ status: string }> => {
  const payload = reason ? { reason } : undefined;
  return post<{ status: string }>(`/offers/decline/${encodeURIComponent(signedData)}/`, payload);
};

/**
 * Accept an offer (authenticated)
 */
export const acceptOffer = async (offerId: string): Promise<{ status: string }> => {
  return post<{ status: string }>(`/offers/${offerId}/accept/`);
};

/**
 * Accept an offer with signature (public)
 */
export const acceptOfferWithSignature = async (signedData: string): Promise<{ status: string }> => {
  return post<{ status: string }>(`/offers/accept/${encodeURIComponent(signedData)}/`);
};

export interface OfferDocumentResult {
  has_document: boolean;
  filename: string | null;
}

/**
 * Upload/replace an offer's legal document (fillable PDF). Recruiter-only.
 */
export const uploadOfferDocument = async (
  offerId: string,
  file: File
): Promise<OfferDocumentResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const json = await request<OfferDocumentResult>(`/offers/${offerId}/document/`, {
    method: 'POST',
    body: formData,
  });
  return unwrapData(json);
};

/**
 * One piece of an offer's combined legal document. The offer's document of
 * record is the ordered concatenation of these sources (templates + one-off
 * uploads) compiled into a single PDF that gets merged, reviewed, and signed.
 */
export interface OfferDocumentSource {
  id: string;
  kind: 'template' | 'upload';
  name: string;
  order: number;
  source_template_id: string | null;
}

/** The offer's document composition — the ordered source list + summary flags. */
export interface OfferDocumentComposition {
  has_document: boolean;
  filename: string | null;
  sources: OfferDocumentSource[];
}

/**
 * Fetch the offer's document composition (ordered source list). Recruiter-only.
 */
export const getOfferDocumentSources = async (
  offerId: string
): Promise<OfferDocumentComposition> => {
  return get<OfferDocumentComposition>(`/offers/${offerId}/document/sources/`);
};

/**
 * Add a source to the offer's document composition and recompile. Recruiter-only.
 * Pass `{ templateId }` to include a company template, or `{ file }` for a
 * one-off upload. Returns the updated composition.
 */
export const addOfferDocumentSource = async (
  offerId: string,
  source: { templateId: string } | { file: File }
): Promise<OfferDocumentComposition> => {
  const path = `/offers/${offerId}/document/sources/`;
  if ('file' in source) {
    const formData = new FormData();
    formData.append('file', source.file);
    const json = await request<OfferDocumentComposition>(path, {
      method: 'POST',
      body: formData,
    });
    return unwrapData(json);
  }
  return post<OfferDocumentComposition>(path, { template_id: source.templateId });
};

/**
 * Remove a source from the offer's document composition and recompile.
 * Recruiter-only. Returns the updated composition.
 */
export const removeOfferDocumentSource = async (
  offerId: string,
  sourceId: string
): Promise<OfferDocumentComposition> => {
  return del<OfferDocumentComposition>(`/offers/${offerId}/document/sources/${sourceId}/`);
};

/**
 * URL for the recruiter "view as candidate" merged-PDF preview (same-origin,
 * session-authenticated). Pass `download` to force an attachment.
 */
export const offerDocumentPreviewUrl = (offerId: string, download = false): string =>
  `/api/offers/${offerId}/document/preview/${download ? '?download=1' : ''}`;

/**
 * URL for the candidate's merged-PDF document via signed link (AllowAny).
 * Pass `download` to force an attachment instead of inline preview. Once the
 * document is fully signed the backend serves the sealed version at this URL.
 */
export const candidateOfferDocumentUrl = (signedData: string, download = false): string =>
  `/api/offers/view/${encodeURIComponent(signedData)}/document/${download ? '?download=1' : ''}`;

// --- Document signing (Phase 2) -------------------------------------------

/**
 * A widget's bounding box in PDF points as `[x0, y0, x1, y1]`, origin at the
 * page's BOTTOM-left (PDF convention — flip the y axis when mapping to CSS).
 */
export type FieldRect = [number, number, number, number];

/** One page's dimensions in PDF points, for scaling `FieldRect`s onto pixels. */
export interface PageDimensions {
  width: number;
  height: number;
}

/** One required signature block on the offer document, with its signed state. */
export interface RequiredSignatureEntry {
  /** Block identity — the AcroForm field name in the compiled document. */
  field_name: string;
  /** Display-only label (source-disambiguated when names would collide). */
  field_label: string;
  order: number;
  signed: boolean;
  signed_at: string | null;
  /** 0-based page index of the block's widget; null/absent when underivable (e.g. the injected default "Agreement" block). */
  page?: number | null;
  /** The widget /Rect in PDF points; null/absent when underivable. */
  rect?: FieldRect | null;
}

/**
 * One candidate-input field (`fill_<label>`) the signer must complete before the
 * document can be finished — the third field category alongside merge fields and
 * signature blocks. `multiline` fields (e.g. a prior-inventions list) render as a
 * textarea. `value` is the current stored answer (null until filled), so a resumed
 * ceremony pre-populates it.
 */
export interface RequiredInputEntry {
  field_label: string;
  multiline: boolean;
  value: string | null;
  order: number;
  /**
   * Max characters the answer may hold, mirroring the widget's capacity so it
   * still fits the sealed PDF. `null` = no cap. Enforced client-side via
   * `maxLength`; the backend also 422s an oversized answer at save.
   */
  max_chars?: number | null;
  /** 0-based page index of the field's widget; null/absent when underivable. */
  page?: number | null;
  /** The widget /Rect in PDF points; null/absent when underivable. */
  rect?: FieldRect | null;
}

/**
 * The candidate's signing status for the attached legal document. Returned by
 * both the signing-state GET and the sign POST (identical shape).
 */
/**
 * Candidate-safe branding + human contact for the standalone /sign page.
 * Every field renders on a public page — never carries offer values.
 */
export interface SigningBranding {
  company_name: string | null;
  primary_color: string | null;
  logo: string | null;
  logo_mimetype: string | null;
  recruiter_name: string | null;
  recruiter_email: string | null;
  candidate_first_name: string | null;
  role_title: string | null;
}

/**
 * The NDA the candidate owes before the documents open, if any.
 *
 * `required && !signed` is the only state that gates: the signing-state
 * endpoint stays reachable through it (that is how the page knows to show the
 * NDA at all), while the document bytes and the sign call are refused
 * server-side with `NDA_REQUIRED` until it clears. Text is null when the
 * company requires no NDA.
 */
export interface SigningNDA {
  required: boolean;
  signed: boolean;
  title: string | null;
  content: string | null;
}

export interface DocumentSigningState {
  required_signatures: RequiredSignatureEntry[];
  required_inputs: RequiredInputEntry[];
  /**
   * Per-page dimensions in PDF points, in page order. Empty/absent when the
   * document couldn't be measured — the ceremony then uses the form view.
   */
  pages?: PageDimensions[];
  completed: boolean;
  completed_at: string | null;
  consent_text: string;
  consent_text_version: string;
  signer_name: string;
  signer_email: string;
  branding?: SigningBranding;
  nda: SigningNDA;
}

/**
 * Which candidate token family a ceremony call runs under:
 * - 'offer' — the classic offer-view link (`/offers/view/...` endpoints)
 * - 'sign'  — the document-scoped signing-only link (`/offers/sign/...`),
 *   minted under a distinct signing salt; it can never open the offer view.
 * Every ceremony fn threads this — hardcoding one prefix silently sends the
 * standalone page's calls to the wrong-salt endpoints (422).
 */
export type SigningLinkKind = 'offer' | 'sign';

const signingBasePath = (signedData: string, linkKind: SigningLinkKind): string =>
  `/offers/${linkKind === 'sign' ? 'sign' : 'view'}/${encodeURIComponent(signedData)}`;

/** One signature the candidate applies to a specific block (keyed by field_name). */
export interface SignatureInput {
  field_name: string;
  mode: 'typed' | 'drawn';
  /** Present when mode === 'typed'. */
  signature_text?: string;
  /** `data:image/png;base64,...` present when mode === 'drawn'. */
  signature_image?: string;
}

/** One candidate-input answer the signer submits for a `fill_<label>` field. */
export interface CandidateInput {
  field_label: string;
  value: string;
}

/** Payload for signing one or more required blocks in a single request. */
export interface SignOfferDocumentPayload {
  signer_name: string;
  signer_email: string;
  consent: boolean;
  signatures: SignatureInput[];
  /** Answers for the document's `fill_<label>` candidate-input fields. */
  inputs?: CandidateInput[];
}

/**
 * Fetch the candidate's document signing status via signed link (AllowAny).
 */
export const getDocumentSigningState = async (
  signedData: string,
  linkKind: SigningLinkKind = 'offer'
): Promise<DocumentSigningState> =>
  get<DocumentSigningState>(`${signingBasePath(signedData, linkKind)}/document/signing-state/`);

/**
 * Submit one or more signatures for the offer document (AllowAny, signed link).
 * Already-signed blocks are skipped server-side, so this is replay-safe.
 * Returns the updated signing state.
 */
export const signOfferDocument = async (
  signedData: string,
  payload: SignOfferDocumentPayload,
  linkKind: SigningLinkKind = 'offer'
): Promise<DocumentSigningState> =>
  post<DocumentSigningState>(`${signingBasePath(signedData, linkKind)}/document/sign/`, payload);

/**
 * URL for the document PDF via the signing-only link (AllowAny, scoped salt).
 * Serves the sealed version once complete — and keeps serving it even after
 * the offer expires or is rescinded (executed-contract access guarantee).
 */
export const scopedOfferDocumentUrl = (signedData: string, download = false): string =>
  `/api/offers/sign/${encodeURIComponent(signedData)}/document/${download ? '?download=1' : ''}`;

// --- Recruiter document visibility -----------------------------------------

/** Badge-grade document status states, in UI precedence order. */
export type DocumentBadgeStatus =
  | 'draft'
  | 'sent'
  | 'opened'
  | 'partially_signed'
  | 'signed'
  | 'needs_attention';

/** One signature block's recruiter-facing progress row. */
export interface DocumentStatusSignature {
  field_name: string;
  field_label: string;
  order: number;
  signer_name: string | null;
  signer_email: string | null;
  signed_at: string | null;
}

/**
 * Merge-field resolution counts carried on the status payload so the drawer's
 * `needs_resolution` state costs no second fetch — and so its count and the
 * resolver's list can never disagree (same server-side builder).
 *
 * Optional on the type because a client build can outlive a server that
 * predates Stage 2; absent reads as "nothing to resolve", never as a blocker.
 */
export interface OfferDocumentResolutionSummary {
  total: number;
  resolved: number;
  unresolved: number;
  needs_reresolution: number;
  needs_resolution: boolean;
  enforcement_active: boolean;
}

/**
 * The recruiter drawer/card payload: delivery + signing progress. Authoritative
 * (endpoint-grade staleness); the list badge is the cheap approximation.
 */
export interface OfferDocumentStatus {
  has_document: boolean;
  sources: { name: string; kind: 'template' | 'upload'; order: number }[];
  lifecycle: 'none' | 'draft' | 'published' | 'stale';
  signatures: DocumentStatusSignature[];
  required_inputs_complete: boolean | null;
  completed_at: string | null;
  signing_link_generated_at: string | null;
  signing_requested_at: string | null;
  signing_request_failed_at: string | null;
  document_first_viewed_at: string | null;
  offer_expired: boolean;
  offer: {
    status: string;
    published_at: string | null;
    opened_first_time_at: string | null;
    last_viewed_at: string | null;
  };
  resolution?: OfferDocumentResolutionSummary;
  /**
   * The company requires an NDA the candidate hasn't signed yet. Not a
   * blocker — both links open on the NDA and reveal the documents after — but
   * it is a step in the flow the recruiter is handing over, so the copy says so.
   */
  nda_pending: boolean;
}

/** Fetch the recruiter's document status for an offer. Recruiter-only. */
export const getOfferDocumentStatus = async (offerId: string): Promise<OfferDocumentStatus> =>
  get<OfferDocumentStatus>(`/offers/${offerId}/document/status/`);

/**
 * URL for the recruiter's sealed-copy download (session-authenticated;
 * 409 until signing completes). Served as an attachment with a friendly name.
 */
export const signedDocumentDownloadUrl = (offerId: string): string =>
  `/api/offers/${offerId}/document/signed/`;

export interface DocumentSigningLinkResult {
  signing_url: string;
  message: string;
}

/**
 * Mint a signing-only link for the offer's documents (screenshare flow).
 * Runs the full send-block/refresh/publish pipeline server-side and pins the
 * document; inert on offer status.
 */
export const generateDocumentSigningLink = async (
  offerId: string
): Promise<DocumentSigningLinkResult> => {
  const response = await request<{ signing_url: string }>(
    `/offers/${offerId}/generate-document-link/`,
    { method: 'POST' }
  );
  const result = unwrapData(response);
  return {
    signing_url: result.signing_url,
    message: (response.meta?.message as string | undefined) ?? 'Signing link generated',
  };
};

export interface SendDocumentResult {
  sent: boolean;
  signing_url: string;
  /** Non-empty when the email failed — surface as a destructive toast. */
  warnings: string[];
  message: string;
}

/**
 * Email the candidate a signing-only link. Uses `request()` (NOT the plain
 * `post` helper) because the SMTP-failure warning travels in `meta.warnings`,
 * which the data-unwrapping helpers discard.
 */
export const sendDocumentForSigning = async (offerId: string): Promise<SendDocumentResult> => {
  const response = await request<{ sent: boolean; signing_url: string }>(
    `/offers/${offerId}/document/send/`,
    { method: 'POST' }
  );
  const result = unwrapData(response);
  return {
    sent: result.sent,
    signing_url: result.signing_url,
    warnings: (response.meta?.warnings as string[] | undefined) ?? [],
    message: (response.meta?.message as string | undefined) ?? '',
  };
};

/**
 * Delete an offer
 */
export const deleteOffer = async (offerId: string): Promise<OfferDeleteResult> => {
  return del<OfferDeleteResult>(`/offers/${offerId}/delete/`);
};

/**
 * Update an offer
 */
export const updateOffer = async (offerId: string, offerData: OfferSubmitData): Promise<Offer> => {
  // Note: Validation is now handled by the backend, which will return Problem+JSON errors
  const result = await put<Offer>(`/offers/${offerId}/update/`, offerData);
  return result;
};

/**
 * Generate a shareable link for an offer
 */
export const generateOfferLink = async (offerId: string): Promise<OfferLinkResult> => {
  const response = await request<OfferLinkResult>(`/offers/${offerId}/generate-link/`, {
    method: 'POST',
  });

  const result = unwrapData(response);
  const metaMessage =
    (response.meta?.message as string | undefined) ?? 'Offer link generated successfully';

  const context = result.context ?? {
    candidate_name: '',
    candidate_email: '',
    role: '',
    company_name: '',
    expiry_date: '',
  };

  return {
    message: metaMessage,
    offer_url: result.offer_url,
    context: {
      candidate_name: context.candidate_name,
      candidate_email: context.candidate_email,
      role: context.role,
      company_name: context.company_name,
      expiry_date: context.expiry_date,
    },
  };
};

/**
 * Sign NDA for an offer.
 *
 * `linkKind` picks the token family, the same way the ceremony calls do: the
 * offer link's NDA path and the signing-only link's both land on the same NDA
 * record, and a token minted for one salt is rejected by the other's endpoint.
 */
export const signNDA = async (
  signedData: string,
  candidateName: string,
  candidateEmail: string,
  linkKind: SigningLinkKind = 'offer'
): Promise<{ nda_signed: boolean; already_signed?: boolean }> => {
  // `post` unwraps the envelope's `data`; the human message rides in `meta`,
  // so it is deliberately absent here. `already_signed` marks the idempotent
  // replay (SignNDAResponseSerializer).
  const path =
    linkKind === 'sign'
      ? `${signingBasePath(signedData, linkKind)}/nda/`
      : `/offers/sign-nda/${encodeURIComponent(signedData)}/`;
  return post<{ nda_signed: boolean; already_signed?: boolean }>(path, {
    candidate_name: candidateName,
    candidate_email: candidateEmail,
  });
};

/**
 * Change offer status
 */
export const changeOfferStatus = async (
  offerId: string,
  newStatus: string,
  timestamp?: string
): Promise<{ status: string }> => {
  const body: { status: string; timestamp?: string } = {
    status: newStatus,
  };

  if (timestamp) {
    body.timestamp = timestamp;
  }

  return post<{ status: string }>(`/offers/${offerId}/change-status/`, body);
};
